import { type SVGProps, type ReactNode } from 'react';

/*
 * The Oxide icon set.
 *
 * Icons do the work photographs would, so they are drawn to one grammar and
 * held to it: a 24x24 grid with a 2px safe margin, built from orthogonal and
 * 45-degree segments only, square caps, mitred joins. The result reads as
 * technical notation rather than illustration — the jar is a rectangle with a
 * ruled lid, the clock is a square with two hands.
 *
 * One weight throughout, the 1px hairline. Each icon inherits `currentColor`,
 * so it always matches the ink of the text beside it. Cook mode renders them
 * at 32px and above, where `.icon--cook` doubles the stroke so the hairline
 * does not disappear at arm's length.
 */

type IconProps = SVGProps<SVGSVGElement>;

function createIcon(paths: ReactNode) {
	return function Icon({ className, ...props }: IconProps) {
		return (
			<svg
				className={className ? `icon ${className}` : 'icon'}
				viewBox='0 0 24 24'
				fill='none'
				stroke='currentColor'
				strokeWidth={1}
				strokeLinecap='square'
				strokeLinejoin='miter'
				aria-hidden='true'
				focusable='false'
				{...props}
			>
				{paths}
			</svg>
		);
	};
}

export const SearchIcon = createIcon(
	<>
		<path d='M4.5 4.5h10v10h-10z' />
		<path d='M14.5 14.5l5 5' />
	</>
);

/** Save a recipe. A bookmark, not a heart: this is filing, not affection. */
export const BookmarkIcon = createIcon(<path d='M7 3.5h10v17l-5-5-5 5z' />);

export const ClockIcon = createIcon(
	<>
		<path d='M4.5 4.5h15v15h-15z' />
		<path d='M12 12V7.5' />
		<path d='M12 12h4' />
	</>
);

export const UsersIcon = createIcon(
	<>
		<path d='M4.5 6.5h9v9h-9z' />
		<path d='M10.5 9.5h9v9h-9' />
	</>
);

export const PlusIcon = createIcon(
	<>
		<path d='M12 4.5v15' />
		<path d='M4.5 12h15' />
	</>
);

export const TrashIcon = createIcon(
	<>
		<path d='M5 6.5h14' />
		<path d='M9.5 6.5V4h5v2.5' />
		<path d='M7 6.5v13h10v-13' />
		<path d='M10.5 10v6' />
		<path d='M13.5 10v6' />
	</>
);

export const PencilIcon = createIcon(
	<>
		<path d='M5 19l1-4 9.5-9.5 3 3L9 18z' />
		<path d='M14 7l3 3' />
	</>
);

export const CheckIcon = createIcon(<path d='M5 12.5l5 5 9-11' />);

export const MinusIcon = createIcon(<path d='M5 12h14' />);

export const ChevronRightIcon = createIcon(<path d='M9 5l7 7-7 7' />);

export const ChevronLeftIcon = createIcon(<path d='M15 5l-7 7 7 7' />);

/** Inspiration. A spark built from straight strokes, so it stays in grammar. */
export const SparklesIcon = createIcon(
	<>
		<path d='M12 3.5v5' />
		<path d='M12 15.5v5' />
		<path d='M3.5 12h5' />
		<path d='M15.5 12h5' />
		<path d='M7 7l3 3' />
		<path d='M14 14l3 3' />
		<path d='M17 7l-3 3' />
		<path d='M10 14l-3 3' />
	</>
);

/** The pantry, and the shopping list it produces. */
export const BasketIcon = createIcon(
	<>
		<path d='M4.5 8.5h15v11h-15z' />
		<path d='M8.5 8.5V5h7v3.5' />
		<path d='M4.5 12.5h15' />
	</>
);

/** A list with two ticked boxes: the shopping list, from the design system's set. */
export const ListIcon = createIcon(
	<>
		<path d='M5.5 3.5h13v17h-13z' />
		<path d='M8 7.5h3v3h-3z' />
		<path d='M8 13.5h3v3h-3z' />
		<path d='M12.5 9h4' />
		<path d='M12.5 15h4' />
	</>
);

export const CloudUploadIcon = createIcon(
	<>
		<path d='M12 20.5V8' />
		<path d='M8 12l4-4 4 4' />
		<path d='M4.5 4h15' />
	</>
);

/** Import a recipe: into the tray, not out of it. */
export const DownloadIcon = createIcon(
	<>
		<path d='M12 3.5v9.5' />
		<path d='M8 9.5l4 4 4-4' />
		<path d='M4.5 15.5v5h15v-5' />
	</>
);

export const LinkIcon = createIcon(
	<>
		<path d='M10 14l4-4' />
		<path d='M8.5 12.5l-3 3v4h4l3-3' />
		<path d='M15.5 11.5l3-3v-4h-4l-3 3' />
	</>
);

/** The theme control. A square, half lit. */
export const SunMoonIcon = createIcon(
	<>
		<path d='M4.5 4.5h15v15h-15z' />
		<path d='M12 8.5h3.5v7H12z' />
	</>
);

export const XIcon = createIcon(
	<>
		<path d='M6 6l12 12' />
		<path d='M18 6L6 18' />
	</>
);

export const MenuIcon = createIcon(
	<>
		<path d='M4.5 7h15' />
		<path d='M4.5 12h15' />
		<path d='M4.5 17h15' />
	</>
);

/** A step timer in cook mode. */
export const TimerIcon = createIcon(
	<>
		<path d='M9 2.5h6' />
		<path d='M12 2.5v3' />
		<path d='M5.5 8.5h13v12h-13z' />
		<path d='M12 15.5v-4' />
	</>
);

/** Heat — the entry point to cook mode. */
export const FlameIcon = createIcon(
	<>
		<path d='M12 3.5l4.5 6v4l-4.5 5-4.5-5v-4z' />
		<path d='M12 11.5v5' />
	</>
);
